import React from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * Reusable weekly availability editor.
 * Shared between:
 *   - Student "Horarios" tab (admin)
 *   - Teacher dialog (admin)
 *   - Public availability questionnaire
 * Value shape: { monday: [{start:"HH:MM", end:"HH:MM"}], tuesday: [...], ... }
 */
export const WEEKDAYS = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

export function emptySlots() {
  return WEEKDAYS.reduce((acc, d) => ({ ...acc, [d.key]: [] }), {});
}

export default function WeeklySlotsEditor({ value, onChange, testidPrefix = "wsslot", disabled = false }) {
  const slots = value || emptySlots();

  const update = (dayKey, next) => {
    if (disabled) return;
    onChange({ ...slots, [dayKey]: next });
  };

  const updateSlot = (dayKey, idx, field, val) => {
    const next = (slots[dayKey] || []).map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    update(dayKey, next);
  };

  const addSlot = (dayKey) => {
    const daySlots = slots[dayKey] || [];
    const suggested = daySlots.length === 0
      ? { start: "09:00", end: "13:00" }
      : { start: "16:00", end: "20:00" };
    update(dayKey, [...daySlots, suggested]);
  };

  const removeSlot = (dayKey, idx) => {
    update(dayKey, (slots[dayKey] || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      {WEEKDAYS.map((d) => {
        const dayList = slots[d.key] || [];
        return (
          <div
            key={d.key}
            className="rounded-xl border border-neutral-200 bg-white p-2.5"
            data-testid={`${testidPrefix}-${d.key}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-20 text-xs font-bold uppercase tracking-wider text-neutral-500">
                {d.label}
              </div>
              <div className="flex-1 flex flex-wrap gap-2 items-center">
                {dayList.length === 0 && (
                  <span className="text-[11px] text-neutral-400 italic">Sin disponibilidad</span>
                )}
                {dayList.map((s, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-neutral-50 px-1.5 py-1"
                    data-testid={`${testidPrefix}-${d.key}-slot-${i}`}
                  >
                    <input
                      type="time"
                      disabled={disabled}
                      value={s.start || ""}
                      onChange={(e) => updateSlot(d.key, i, "start", e.target.value)}
                      className="h-7 rounded border border-neutral-300 bg-white px-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-orange-500"
                      data-testid={`${testidPrefix}-${d.key}-start-${i}`}
                    />
                    <span className="text-neutral-400 text-xs">–</span>
                    <input
                      type="time"
                      disabled={disabled}
                      value={s.end || ""}
                      onChange={(e) => updateSlot(d.key, i, "end", e.target.value)}
                      className="h-7 rounded border border-neutral-300 bg-white px-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-orange-500"
                      data-testid={`${testidPrefix}-${d.key}-end-${i}`}
                    />
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeSlot(d.key, i)}
                        className="text-red-500 hover:text-red-700 pl-0.5"
                        title="Quitar franja"
                        data-testid={`${testidPrefix}-${d.key}-remove-${i}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => addSlot(d.key)}
                  className="inline-flex items-center gap-1 h-7 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700 px-2 text-[11px] font-bold"
                  data-testid={`${testidPrefix}-${d.key}-add`}
                >
                  <Plus className="h-3 w-3" /> Añadir
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
